// ./src/pages/private/PlayListsPage/PlayListsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import { PlayListFormModal, PlayListsTable } from "@/components/features";
import { usePlayLists } from "@/hooks";
import { PlayList } from "@/utils/api";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const PlayListsPage: React.FC<PageProps> = (props: PageProps) => {
  const navigate = useNavigate();

  // Use the playlists hook for all data management
  const {
    // Data state
    playLists,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingPlayListId,
    selectedPlayList,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,
    reset,

    // UI state management
    setSelectedPlayList,

    // Error handling
    lastError,
    clearError,
  } = usePlayLists({
    autoLoad: false,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [playListToDelete, setPlayListToDelete] = useState<PlayList | null>(
    null
  );
  const [searchValue, setSearchValue] = useState<string>("");

  // Effect to load displays when venue changes
  useEffect(() => {
    reset();
    // Apply search filter
    const newFilters: any = {};

    if (searchValue.trim()) {
      newFilters.searchKeyword = searchValue.trim();
    }

    list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
  }, [searchValue, reset, list, sortBy]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Apply search filter
      const newFilters: any = {};

      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      }

      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [sortBy, setFilters, list]
  );

  // Handle add playlist button click
  const handleAddPlayList = useCallback(() => {
    setSelectedPlayList(null);
    setFormModalOpened(true);
  }, [setSelectedPlayList]);

  // Handle edit playlist
  const handleEditPlayList = useCallback(
    (playList: PlayList) => {
      setSelectedPlayList(playList);
      setFormModalOpened(true);
    },
    [setSelectedPlayList]
  );

  // Handle duplicate playlist
  const handleDuplicatePlayList = useCallback(
    (playList: PlayList) => {
      setSelectedPlayList({
        ...playList,
        id: undefined,
        name: `${playList.name} (Copy)`,
        duplicateOfId: playList.id,
      });
      setFormModalOpened(true);
    },
    [setSelectedPlayList]
  );

  // Handle go into playlist (navigate to items page)
  const handleGoIntoPlayList = useCallback(
    (playList: PlayList) => {
      if (playList.id) {
        navigate(`/play-lists/${playList.id}/items`);
      }
    },
    [navigate]
  );

  // Handle remove playlist button click
  const handleRemovePlayListClick = useCallback((playList: PlayList) => {
    setPlayListToDelete(playList);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (playListToDelete?.id) {
      const success = await remove(playListToDelete.id);
      if (success) {
        setDeleteModalOpened(false);
        setPlayListToDelete(null);
      }
    }
  }, [playListToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setPlayListToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedPlayList(null);
    clearError();
  }, [setSelectedPlayList, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle playlist creation
  const handleCreatePlayList = useCallback(
    async (playListData: any) => {
      const result = await create(playListData);
      if (result) {
        setFormModalOpened(false);
        setSelectedPlayList(null);
      }
      return result;
    },
    [create, setSelectedPlayList]
  );

  // Handle playlist update
  const handleUpdatePlayList = useCallback(
    async (playListId: string, playListData: any) => {
      const result = await update(playListId, playListData);
      if (result) {
        setFormModalOpened(false);
        setSelectedPlayList(null);
      }
      return result;
    },
    [update, setSelectedPlayList]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-playlist"
                  iconName="Plus"
                  onClick={handleAddPlayList}
                  disabled={isCreating || !!removingPlayListId || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingPlayListId || loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search playlists..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Playlists table */}
        <PlayListsTable
          playLists={playLists}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditPlayList={handleEditPlayList}
          onDuplicatePlayList={handleDuplicatePlayList}
          onRemovePlayList={handleRemovePlayListClick}
          onGoIntoPlayList={handleGoIntoPlayList}
          removingPlayListId={removingPlayListId}
          size="md"
        />

        {/* Empty state when no playlists */}
        <EmptyState
          display={isEmpty && !loading}
          title="No playlists yet"
          description="Start by creating your first playlist"
          actionIcon="Plus"
          actionLabel="Add Playlist"
          onAction={handleAddPlayList}
        />
      </Stack>

      {/* Playlist form modal for create/edit */}
      <PlayListFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        playList={selectedPlayList}
        onCreatePlayList={handleCreatePlayList}
        onUpdatePlayList={handleUpdatePlayList}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Playlist"
        message={`Are you sure you want to delete "${playListToDelete?.name}"?`}
        details="This action cannot be undone. The playlist and all its items will be permanently removed."
        confirmInProgress={removingPlayListId === playListToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
